package com.example.histoire.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.alt
import org.w3c.files.FileReader
import org.w3c.dom.HTMLInputElement
import kotlin.js.unsafeCast

/** Horizontal padding of `appCard()` in main.kt — keep in sync for profile rows that bleed to the card edges */
private const val APP_CARD_INNER_PADDING_PX = 18
private const val FORM_FOCUS_SCROLL_JS =
    "this.scrollIntoView({behavior:'smooth', block:'center'});"
private const val FORM_ENTER_NEXT_JS =
    "if(event.key==='Enter'){event.preventDefault();const scope=this.closest('[data-enter-scope]')||document;" +
        "const els=[...scope.querySelectorAll('input,button,textarea,select')]" +
        ".filter(e=>!e.disabled&&e.offsetParent!==null);" +
        "const i=els.indexOf(this);if(i>=0&&i<els.length-1){els[i+1].focus({preventScroll:true});" +
        "els[i+1].scrollIntoView({behavior:'smooth',block:'center'});}}"

private data class DraftRow(
    val id: String,
    val firstName: String,
    /** Affichage JJ/MM/AAAA (masque automatique pendant la saisie). */
    val birthDateInput: String,
)

private fun capitalizeLeadingLetter(value: String): String {
    val first = value.firstOrNull() ?: return value
    return if (first.isLetter()) {
        first.titlecaseChar() + value.drop(1)
    } else {
        value
    }
}

@Composable
fun ChildProfilesOnboarding(
    initialProfiles: List<StoredChildProfile>,
    initialBackgroundDataUrl: String?,
    allowDismiss: Boolean,
    onComplete: (List<StoredChildProfile>, String?) -> Unit,
    onDismiss: () -> Unit,
) {
    val initialReaderPrefs = remember { loadReaderUiPrefs() }
    var drafts by remember(initialProfiles.map { it.id }.joinToString()) {
        mutableStateOf(
            if (initialProfiles.isEmpty()) {
                listOf(DraftRow(id = newChildProfileId(), firstName = "", birthDateInput = ""))
            } else {
                initialProfiles.map { DraftRow(it.id, it.firstName, isoToFrenchBirthDisplay(it.birthDateIso)) }
            },
        )
    }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var backgroundDataUrl by remember { mutableStateOf(initialBackgroundDataUrl.orEmpty()) }
    var step by remember { mutableStateOf(1) }
    var textMode by remember { mutableStateOf(initialReaderPrefs.textMode) } // auto | light | dark | custom
    var customTextColor by remember { mutableStateOf(initialReaderPrefs.textColorHex) }
    var useCardFrame by remember { mutableStateOf(initialReaderPrefs.backdropEnabled) }

    fun openBackgroundFilePicker() {
        val inp = document.createElement("input").unsafeCast<HTMLInputElement>()
        inp.type = "file"
        inp.accept = "image/png,image/jpeg,image/webp,image/gif"
        inp.addEventListener(
            "change",
            listener@{ _: org.w3c.dom.events.Event ->
                val file = inp.files?.item(0) ?: return@listener
                val reader = FileReader()
                reader.onload = { _: org.w3c.dom.events.Event ->
                    val result = reader.result
                    if (result is String) {
                        backgroundDataUrl = result
                    }
                }
                reader.readAsDataURL(file)
            },
        )
        inp.click()
    }

    fun todayLocal(): LocalDate {
        val d = kotlin.js.Date()
        return LocalDate(
            year = d.getFullYear().toInt(),
            monthNumber = d.getMonth().toInt() + 1,
            dayOfMonth = d.getDate().toInt(),
        )
    }

    fun validate(): List<StoredChildProfile>? {
        val today = todayLocal()
        val out = mutableListOf<StoredChildProfile>()
        for (row in drafts) {
            val name = row.firstName.trim()
            if (name.isEmpty()) {
                errorMessage = "Merci de renseigner le prénom pour chaque profil."
                return null
            }
            val birthRaw = row.birthDateInput.trim()
            if (birthRaw.isEmpty()) {
                errorMessage = "Merci de renseigner la date de naissance pour chaque enfant."
                return null
            }
            val birth =
                parseBirthDateFlexible(birthRaw) ?: run {
                    errorMessage =
                        "Date de naissance invalide pour $name. Utilise une date complète au format JJ/MM/AAAA (ex. 15/03/2018)."
                    return null
                }
            if (birth > today) {
                errorMessage = "La date de naissance de $name ne peut pas être dans le futur."
                return null
            }
            out.add(StoredChildProfile(id = row.id, firstName = name, birthDateIso = birth.toString()))
        }
        errorMessage = null
        return out
    }

    fun hasAtLeastOneChildName(): Boolean = drafts.any { it.firstName.trim().isNotEmpty() }

    fun saveVisualPrefs() {
        val safeMode = if (textMode in setOf("auto", "light", "dark", "custom")) textMode else "auto"
        val safeHex = customTextColor.trim().ifBlank { "#F8FAFC" }
        saveReaderUiPrefs(
            initialReaderPrefs.copy(
                textMode = safeMode,
                textColorHex = safeHex,
                backdropEnabled = useCardFrame,
            ),
        )
    }

    Div({ style { welcomeHeroColumn() } }) {
        Div({ style { welcomeHeroImgWrap() } }) {
            Img(src = "/histoire-auth-hero.png", attrs = {
                alt("Histoire du Soir — livre magique")
                style { welcomeBookHeroImg() }
            })
        }
        Div({ style { welcomeHeaderTextColumn() } }) {
            H1({ style { onboardingTitle() } }) {
                Text("Bienvenue dans ton univers personnalisé")
            }
            P({ style { onboardingBody() } }) {
                Text(
                    "Une petite initialisation est nécessaire : indique le prénom et la date de naissance " +
                        "de chaque enfant. L'âge sera calculé automatiquement à chaque visite.",
                )
            }
        }
    }

    P({ style { onboardingStepTitle() } }) {
        Text(
            when (step) {
                1 -> "Étape 1/4 · Ma famille"
                2 -> "Étape 2/4 · Image de fond"
                3 -> "Étape 3/4 · Vérification"
                else -> "Étape 4/4 · Style du texte"
            },
        )
    }

    if (step == 1) {
        drafts.forEachIndexed { index, row ->
            Div({
                attr("data-enter-scope", "child-profile-row")
                style { profileBlock() }
            }) {
                P({ style { onboardingLabel() } }) { Text("Enfant ${index + 1}") }
                P({ style { onboardingLabel() } }) { Text("Prénom") }
                Input(type = InputType.Text, attrs = {
                    value(row.firstName)
                    onInput { event ->
                        val normalized = capitalizeLeadingLetter(event.value)
                        drafts = drafts.mapIndexed { i, r ->
                            if (i == index) r.copy(firstName = normalized) else r
                        }
                    }
                    attr("onfocus", FORM_FOCUS_SCROLL_JS)
                    attr("onkeydown", FORM_ENTER_NEXT_JS)
                    attr("placeholder", "Prénom")
                    style { onboardingInput() }
                })
                P({ style { onboardingLabel() } }) { Text("Date de naissance") }
                Input(type = InputType.Text, attrs = {
                    value(row.birthDateInput)
                    onInput { event ->
                        val masked = maskFrenchBirthDateInput(event.value)
                        drafts = drafts.mapIndexed { i, r ->
                            if (i == index) r.copy(birthDateInput = masked) else r
                        }
                    }
                    attr("onfocus", FORM_FOCUS_SCROLL_JS)
                    attr("onkeydown", FORM_ENTER_NEXT_JS)
                    attr("placeholder", "JJ/MM/AAAA")
                    attr("inputmode", "numeric")
                    attr("autocomplete", "bday")
                    attr("maxlength", "10")
                    style { onboardingInput() }
                })
                P({ style { onboardingFieldHint() } }) {
                    Text("Tape uniquement les chiffres : les \"/\" s'insèrent automatiquement.")
                }
                if (drafts.size > 1) {
                    Button(attrs = {
                        onClick {
                            drafts = drafts.filterIndexed { i, _ -> i != index }
                            errorMessage = null
                        }
                        style { removeProfileButton() }
                    }) { Text("Retirer ce profil") }
                }
            }
        }

        Button(attrs = {
            onClick {
                drafts = drafts + DraftRow(id = newChildProfileId(), firstName = "", birthDateInput = "")
                errorMessage = null
            }
            style { addProfileButton() }
        }) { Text("+ Ajouter un profil") }
    }

    if (step == 2) {
        Div({ style { backgroundPhotoSlot() } }) {
            P({ style { backgroundPhotoSlotTitle() } }) { Text("Catégorie : image de fond") }
            P({ style { backgroundPhotoSlotBody() } }) {
                Text(
                    "Cette image sera utilisée comme fond sur le formulaire de génération " +
                        "et sur la page de lecture de l'histoire.",
                )
            }
            P({ style { onboardingFieldHint() } }) {
                Text(
                    if (backgroundDataUrl.isBlank()) "Aucune image sélectionnée pour le moment."
                    else "Image sélectionnée avec succès.",
                )
            }
            Div({ style { backgroundPhotoRow() } }) {
                Button(attrs = {
                    onClick { openBackgroundFilePicker() }
                    style { chooseBackgroundButton() }
                }) { Text(if (backgroundDataUrl.isBlank()) "Choisir une image" else "Changer l'image") }
                if (backgroundDataUrl.isNotBlank()) {
                    Button(attrs = {
                        onClick { backgroundDataUrl = "" }
                        style { removeBackgroundButton() }
                    }) { Text("Retirer la photo") }
                }
            }
            if (backgroundDataUrl.isNotBlank()) {
                Div({ style { backgroundPreviewWrap() } }) {
                    Img(src = backgroundDataUrl, attrs = {
                        alt("Aperçu du fond d'écran")
                        style { backgroundPreviewImg() }
                    })
                }
            }
        }
    }

    if (step == 3) {
        val namedChildren = drafts.mapNotNull { row ->
            val name = row.firstName.trim()
            if (name.isEmpty()) null else "${name} · ${row.birthDateInput.trim().ifBlank { "Date non saisie" }}"
        }
        Div({ style { recapBlock() } }) {
            P({ style { onboardingLabel() } }) { Text("Vérification avant validation") }
            P({ style { onboardingBody() } }) {
                Text("Vérifie les informations ci-dessous avant de passer à la personnalisation du style.")
            }
            P({ style { onboardingLabel() } }) { Text("Enfants") }
            if (namedChildren.isEmpty()) {
                P({ style { onboardingFieldHint() } }) { Text("Aucun profil renseigné pour le moment.") }
            } else {
                namedChildren.forEach { line ->
                    P({ style { recapLine() } }) { Text("• $line") }
                }
            }
            P({ style { onboardingLabel() } }) { Text("Image de fond") }
            P({ style { recapLine() } }) {
                Text(if (backgroundDataUrl.isBlank()) "Aucune image sélectionnée" else "Image personnalisée sélectionnée")
            }
        }
    }

    if (step == 4) {
        Div({ style { recapBlock() } }) {
            P({ style { onboardingLabel() } }) { Text("Style du texte") }
            P({ style { onboardingBody() } }) {
                Text("Choisis un mode de lisibilité. Ces préférences sont appliquées à la lecture des histoires.")
            }
            Div({ style { textModeRow() } }) {
                listOf(
                    "auto" to "Automatique",
                    "light" to "Texte clair",
                    "dark" to "Texte sombre",
                    "custom" to "Couleur perso",
                ).forEach { (mode, label) ->
                    Button(attrs = {
                        onClick { textMode = mode }
                        style { textModeChip(selected = textMode == mode) }
                    }) { Text(label) }
                }
            }
            if (textMode == "custom") {
                P({ style { onboardingLabel() } }) { Text("Couleur personnalisée (hex)") }
                Input(type = InputType.Text, attrs = {
                    value(customTextColor)
                    onInput { customTextColor = it.value }
                    attr("onfocus", FORM_FOCUS_SCROLL_JS)
                    attr("onkeydown", FORM_ENTER_NEXT_JS)
                    attr("placeholder", "#F8FAFC")
                    style { onboardingInput() }
                })
            }
            Div({ style { frameRow() } }) {
                P({ style { onboardingLabel() } }) { Text("Cadre de fond pour améliorer la lisibilité") }
                Button(attrs = {
                    onClick { useCardFrame = !useCardFrame }
                    style { textModeChip(selected = useCardFrame) }
                }) { Text(if (useCardFrame) "Activé" else "Désactivé") }
            }
        }
    }

    if (errorMessage != null) {
        P({ style { onboardingError() } }) { Text(errorMessage!!) }
    }

    Div({ style { onboardingActions() } }) {
        if (allowDismiss) {
            Button(attrs = {
                onClick { onDismiss() }
                style { dismissButton() }
            }) { Text("Annuler") }
        }
        if (step > 1) {
            Button(attrs = {
                onClick { step -= 1 }
                style { dismissButton() }
            }) { Text("Retour") }
        }
        Button(attrs = {
            onClick {
                if (step == 1) {
                    if (!hasAtLeastOneChildName()) {
                        errorMessage = "Ajoute au moins un enfant pour continuer."
                        return@onClick
                    }
                    errorMessage = null
                    step = 2
                    return@onClick
                }
                if (step == 2) {
                    errorMessage = null
                    step = 3
                    return@onClick
                }
                if (step == 3) {
                    val ok = validate() ?: return@onClick
                    if (ok.isEmpty()) {
                        errorMessage = "Ajoute au moins un profil valide."
                        return@onClick
                    }
                    errorMessage = null
                    step = 4
                    return@onClick
                }
                val ok = validate() ?: return@onClick
                saveVisualPrefs()
                val bg = backgroundDataUrl.trim().ifBlank { null }
                onComplete(ok, bg)
            }
            style { continueButton() }
        }) {
            Text(
                when (step) {
                    1 -> "Continuer vers l'image"
                    2 -> "Continuer vers la vérification"
                    3 -> "Continuer vers le style"
                    else -> "Valider"
                },
            )
        }
    }
}

/** Jusqu’à 8 chiffres JJMMAAAA → affichage JJ/MM/AAAA. */
private fun maskFrenchBirthDateInput(raw: String): String {
    val digits = raw.filter { it.isDigit() }.take(8)
    return formatFrenchBirthDigits(digits)
}

private fun formatFrenchBirthDigits(digits: String): String = when {
    digits.isEmpty() -> ""
    digits.length <= 2 -> digits
    digits.length <= 4 -> "${digits.substring(0, 2)}/${digits.substring(2)}"
    else -> "${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4)}"
}

/** Profil déjà en ISO (Supabase) → affichage français pour l’édition. */
private fun isoToFrenchBirthDisplay(iso: String): String {
    val head = iso.trim().substringBefore('T').take(10)
    if (head.isEmpty()) return ""
    val parsed = runCatching { LocalDate.parse(head) }.getOrNull() ?: return ""
    val digits =
        "${parsed.dayOfMonth.toString().padStart(2, '0')}" +
            "${parsed.monthNumber.toString().padStart(2, '0')}" +
            "${parsed.year}"
    return formatFrenchBirthDigits(digits)
}

/** JJ/MM/AAAA, ou 8 chiffres ; ISO conservé pour données / copier-coller. */
private fun parseBirthDateFlexible(raw: String): LocalDate? {
    val t = raw.trim()
    if (t.isEmpty()) return null

    val isoHead = t.substringBefore('T').take(10)
    if (isoHead.matches(Regex("""\d{4}-\d{2}-\d{2}"""))) {
        runCatching { LocalDate.parse(isoHead) }.getOrNull()?.let { return it }
    }

    val digits = t.filter { it.isDigit() }
    if (digits.length == 8) {
        val day = digits.substring(0, 2).toIntOrNull() ?: return null
        val month = digits.substring(2, 4).toIntOrNull() ?: return null
        val year = digits.substring(4, 8).toIntOrNull() ?: return null
        return runCatching { LocalDate(year, month, day) }.getOrNull()
    }

    val m = Regex("""^(\d{1,2})/(\d{1,2})/(\d{4})$""").matchEntire(t) ?: return null
    val day = m.groupValues[1].toIntOrNull() ?: return null
    val month = m.groupValues[2].toIntOrNull() ?: return null
    val year = m.groupValues[3].toIntOrNull() ?: return null
    return runCatching { LocalDate(year, month, day) }.getOrNull()
}

private fun StyleScope.welcomeHeroColumn() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    alignItems(AlignItems.Center)
    gap(16.px)
    width(100.percent)
    property("box-sizing", "border-box")
    /* Fond opaque : appCard() est transparente et le thème page est souvent pastel — sinon le texte sous le livre se noie. */
    padding(18.px, 14.px)
    backgroundColor(Color("#1e293b"))
    property("border-radius", "20px")
    property("border", "1px solid rgba(255, 255, 255, 0.16)")
    property("box-shadow", "0 12px 32px rgba(15, 23, 42, 0.25)")
}

private fun StyleScope.welcomeHeroImgWrap() {
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    width(100.percent)
}

private fun StyleScope.welcomeBookHeroImg() {
    width(168.px)
    maxWidth(52.percent)
    property("aspect-ratio", "1 / 1")
    property("object-fit", "cover")
    property("border-radius", "22px")
    property("box-shadow", "0 12px 40px rgba(250, 204, 21, 0.22)")
    property("border", "1px solid rgba(255,255,255,0.2)")
}

private fun StyleScope.welcomeHeaderTextColumn() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(6.px)
    minWidth(0.px)
    width(100.percent)
    property("text-align", "center")
    padding(4.px, 2.px)
    property("box-sizing", "border-box")
    /* Le bloc parent welcomeHeroColumn porte déjà le fond lisible. */
    backgroundColor(Color.transparent)
}

private fun StyleScope.backgroundPhotoSlot() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    alignItems(AlignItems.Center)
    gap(12.px)
    width(100.percent)
    minWidth(0.px)
    padding(16.px)
    margin(6.px)
    property("box-sizing", "border-box")
    property("border", "2px dashed rgba(148, 163, 184, 0.85)")
    property("border-radius", "16px")
    backgroundColor(Color("#1e293b"))
    property("box-shadow", "inset 0 0 0 1px rgba(255,255,255,0.06)")
}

private fun StyleScope.backgroundPhotoSlotTitle() {
    color(Color("#F8FAFC"))
    fontSize(16.px)
    margin(0.px)
    property("font-weight", "700")
    property("text-align", "center")
    property("line-height", "1.35")
    property(
        "text-shadow",
        "0 1px 2px rgba(15,23,42,0.9)",
    )
}

private fun StyleScope.backgroundPhotoSlotBody() {
    color(Color("#CBD5E1"))
    fontSize(14.px)
    margin(0.px)
    property("text-align", "center")
    property("line-height", "1.45")
    property("max-width", "34rem")
}

private fun StyleScope.backgroundPhotoRow() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Row)
    flexWrap(FlexWrap.Wrap)
    alignItems(AlignItems.Center)
    justifyContent(JustifyContent.Center)
    gap(10.px)
    width(100.percent)
    property("box-sizing", "border-box")
}

private fun StyleScope.chooseBackgroundButton() {
    padding(12.px)
    property("min-height", "46px")
    color(Color.white)
    backgroundColor(Color("#0EA5E9"))
    property("border", "1px solid #7DD3FC")
    property("border-radius", "14px")
    property("font-weight", "700")
    property("font-size", "16px")
}

private fun StyleScope.removeBackgroundButton() {
    padding(12.px)
    property("min-height", "46px")
    color(Color("#FECACA"))
    backgroundColor(Color("rgba(127, 29, 29, 0.35)"))
    property("border", "1px solid rgba(248, 113, 113, 0.45)")
    property("border-radius", "14px")
    property("font-weight", "600")
    property("font-size", "16px")
}

private fun StyleScope.backgroundPreviewWrap() {
    width(100.percent)
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    property("box-sizing", "border-box")
}

private fun StyleScope.backgroundPreviewImg() {
    width(100.percent)
    maxWidth(220.px)
    maxHeight(120.px)
    property("object-fit", "cover")
    property("border-radius", "12px")
    property("border", "1px solid rgba(255,255,255,0.2)")
}

private fun StyleScope.onboardingTitle() {
    margin(2.px)
    color(Color("#F8FAFC"))
    property("text-align", "center")
    property("line-height", "1.15")
    property("font-weight", "800")
    fontSize(22.px)
    property("text-shadow", "0 1px 3px rgba(0, 0, 0, 0.45)")
}

private fun StyleScope.onboardingBody() {
    color(Color("#E2E8F0"))
    fontSize(16.px)
    margin(6.px)
    property("line-height", "1.55")
    property("text-align", "center")
    property("max-width", "36rem")
    property("margin-left", "auto")
    property("margin-right", "auto")
    property("text-shadow", "0 1px 2px rgba(0, 0, 0, 0.4)")
}

private fun StyleScope.onboardingStepTitle() {
    color(Color("#C7D2FE"))
    fontSize(14.px)
    margin(0.px)
    property("font-weight", "700")
    property("letter-spacing", "0.02em")
}

private fun StyleScope.onboardingLabel() {
    color(Color("#F8FAFC"))
    fontSize(15.px)
    margin(4.px)
    property("font-weight", "700")
    property("letter-spacing", "0.02em")
    property("text-shadow", "0 1px 2px rgba(15, 23, 42, 0.85)")
}

private fun StyleScope.profileBlock() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(8.px)
    minWidth(0.px)
    property("margin-left", "-${APP_CARD_INNER_PADDING_PX}px")
    property("margin-right", "-${APP_CARD_INNER_PADDING_PX}px")
    property("width", "calc(100% + ${APP_CARD_INNER_PADDING_PX * 2}px)")
    property("margin-top", "6px")
    property("margin-bottom", "6px")
    padding(14.px)
    backgroundColor(Color("#1e293b"))
    property("border-radius", "12px")
    property("border", "1px solid rgba(226, 232, 240, 0.22)")
    property("box-sizing", "border-box")
    property("box-shadow", "0 8px 24px rgba(15, 23, 42, 0.35)")
}

private fun StyleScope.onboardingInput() {
    width(100.percent)
    padding(12.px)
    backgroundColor(Color("#F8FAFC"))
    color(Color("#0F172A"))
    property("border", "2px solid #CBD5E1")
    property("border-radius", "14px")
    property("box-sizing", "border-box")
    property("font-size", "16px")
    property("font-weight", "500")
    property(
        "box-shadow",
        "inset 0 1px 2px rgba(15, 23, 42, 0.06)",
    )
}

private fun StyleScope.onboardingFieldHint() {
    color(Color("#CBD5E1"))
    fontSize(14.px)
    margin(0.px)
    property("margin-top", "-2px")
    property("line-height", "1.4")
    property("font-weight", "500")
}

private fun StyleScope.addProfileButton() {
    width(100.percent)
    margin(8.px)
    padding(12.px)
    property("min-height", "48px")
    color(Color.white)
    backgroundColor(Color("#8B5CF6"))
    property("border", "1px solid #C4B5FD")
    property("border-radius", "14px")
    property("font-weight", "700")
    property("font-size", "16px")
}

private fun StyleScope.removeProfileButton() {
    property("align-self", "flex-start")
    margin(4.px)
    padding(10.px)
    property("min-height", "44px")
    color(Color("#FECACA"))
    backgroundColor(Color("rgba(127, 29, 29, 0.35)"))
    property("border", "1px solid rgba(248, 113, 113, 0.45)")
    property("border-radius", "12px")
    property("font-weight", "600")
    property("font-size", "15px")
}

private fun StyleScope.onboardingActions() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(10.px)
    width(100.percent)
    margin(12.px)
}

private fun StyleScope.continueButton() {
    width(100.percent)
    padding(14.px)
    property("min-height", "52px")
    color(Color.white)
    backgroundColor(Color("#FB7185"))
    property("border", "1px solid #FECDD3")
    property("border-radius", "16px")
    property("font-weight", "800")
    property("font-size", "16px")
}

private fun StyleScope.dismissButton() {
    width(100.percent)
    padding(12.px)
    property("min-height", "48px")
    color(Color("#E2E8F0"))
    backgroundColor(Color("rgba(255,255,255,0.08)"))
    property("border", "1px solid rgba(255,255,255,0.22)")
    property("border-radius", "14px")
    property("font-weight", "600")
    property("font-size", "16px")
}

private fun StyleScope.onboardingError() {
    color(Color("#FCA5A5"))
    fontSize(14.px)
    margin(6.px)
}

private fun StyleScope.recapBlock() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(8.px)
    width(100.percent)
    padding(14.px)
    backgroundColor(Color("#1e293b"))
    property("border-radius", "12px")
    property("border", "1px solid rgba(226, 232, 240, 0.22)")
    property("box-sizing", "border-box")
}

private fun StyleScope.recapLine() {
    color(Color("#E2E8F0"))
    fontSize(14.px)
    margin(0.px)
    property("line-height", "1.45")
}

private fun StyleScope.textModeRow() {
    display(DisplayStyle.Flex)
    flexWrap(FlexWrap.Wrap)
    gap(8.px)
    width(100.percent)
}

private fun StyleScope.textModeChip(selected: Boolean) {
    padding(10.px, 12.px)
    property("min-height", "42px")
    color(if (selected) Color.white else Color("#E2E8F0"))
    backgroundColor(if (selected) Color("#0EA5E9") else Color("rgba(255,255,255,0.08)"))
    property("border", if (selected) "1px solid #7DD3FC" else "1px solid rgba(255,255,255,0.22)")
    property("border-radius", "999px")
    property("font-weight", "700")
    property("font-size", "14px")
}

private fun StyleScope.frameRow() {
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.FlexStart)
    alignItems(AlignItems.Center)
    flexWrap(FlexWrap.Wrap)
    gap(10.px)
    width(100.percent)
}
